import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Tab, Table, Divider, Icon, Button, Dropdown, Grid, GridRow, GridColumn, Checkbox, Header, Accordion } from "semantic-ui-react";
import _ from "lodash";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListPermissions.scss";
import { size } from "lodash";
import { Permission } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { PermissionItem } from "../PermissionItem";
import { hasPermission, isAdmin, isMaster } from "../../../../utils/checkPermission";
import { MODULES } from "../../../../utils/constants";
import { ErrorAccessDenied } from "../../../../pages/admin/Error";
import { useLanguage } from "../../../../contexts";
import { LoadingSpinner } from "../../../Shared";

const permissionController = new Permission();

export function ListPermissions(props) {
  const { isByRoles, permissionsActive, reload, onReload, onOpenModal } = props;
  const [permissions, setPermissions] = useState(null);
  const [permissionsFilter, setPermissionsFilter] = useState([]);
  const [currentUserPerms, setCurrentUserPerms] = useState([]);

  const { accessToken, user: { role } } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  // Load all permissions
  useEffect(() => {
    (async () => {
      try {
        setPermissions(null);
        const response = await permissionController.getPermissions(accessToken);
        setPermissions(response || []);
        setPermissionsFilter(response || []);
      } catch (error) {
        console.error(error);
        setPermissions([]);
        setPermissionsFilter([]);
      }
    })();
  }, [permissionsActive, reload]);

  // Load current user's permissions
  useEffect(() => {
    (async () => {
      try {
        if (role) {
          const response = await permissionController.getPermissionsByRole(accessToken, role._id, true);
          setCurrentUserPerms(response);
        }
      } catch (error) {
        setCurrentUserPerms([]);
      }
    })();
  }, [role]);

  const canView = isMaster(role) || isAdmin(role) || hasPermission(currentUserPerms, role._id, "permissions", "view");
  if (!canView) return <ErrorAccessDenied />;
  if (permissions === null) return <LoadingSpinner />;
  if (permissions.length === 0) return t("there_is_no_permission");

  // Group by role → by module
  const grouped = permissionsFilter.reduce((acc, perm) => {
    const rName = perm.role?.name || t("noRole");
    const mName = t(perm.module) || t("noModule");
    if (!acc[rName]) acc[rName] = {};
    if (!acc[rName][mName]) acc[rName][mName] = [];
    acc[rName][mName].push(perm);
    return acc;
  }, {});

  const sortedRoles = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

  // --- View 1: by permissions (vertical role tabs + module sub-tabs) ---
  if (!isByRoles) {
    const panes = sortedRoles.map((roleName) => {
      const byModule = grouped[roleName];
      const sortedModules = Object.keys(byModule).sort((a, b) => a.localeCompare(b));

      const subPanes = sortedModules.map((moduleName) => ({
        menuItem: moduleName,
        render: () => (
          <Tab.Pane key={moduleName}>
            <Table className="permissions-table">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("state")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("action")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("actions")}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {byModule[moduleName].map((permission) => (
                  <PermissionItem key={permission._id} permission={permission} onReload={onReload} t={t} />
                ))}
              </Table.Body>
            </Table>
          </Tab.Pane>
        ),
      }));

      return {
        menuItem: roleName,
        render: () => (
          <div className="permissions-module-tabs">
            <Tab menu={{ borderless: true, attached: false }} panes={subPanes} />
          </div>
        ),
      };
    });

    return (
      <div className="permissions-list">
        <div style={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}>
          <SearchPill
            dataOrigin={permissions}
            setData={setPermissionsFilter}
            filterFn={(item, val) =>
              [t(item.module), t(item.action), item.role?.name || ""].some((s) =>
                s.toLowerCase().includes(val.toLowerCase())
              )
            }
            t={t}
          />
          {onOpenModal && (
            <Button icon className="add-perm-btn" style={{ marginLeft: "auto", marginRight: "0.5rem" }} onClick={onOpenModal}>
              <Icon name="plus" />
            </Button>
          )}
        </div>
        <Divider clearing />
        <Tab
          menu={{ fluid: true, vertical: true }}
          menuPosition="left"
          panes={panes}
          style={{ marginTop: "50px" }}
        />
      </div>
    );
  }

  // --- View 2: by roles (flat cards, no accordion) ---
  return (
    <div className="permissions-list">
      <div style={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}>
        <SearchPill
          dataOrigin={permissions}
          setData={setPermissionsFilter}
          filterFn={(item, val) =>
            (item.role?.name || "").toLowerCase().includes(val.toLowerCase())
          }
          t={t}
        />
        {onOpenModal && (
          <Button icon className="add-perm-btn" style={{ marginLeft: "auto", marginRight: "0.5rem" }} onClick={onOpenModal}>
            <Icon name="plus" />
          </Button>
        )}
      </div>
      <Divider clearing />
      <div className="role-cards-list">
        {sortedRoles.map((roleName) => (
          <RoleCard
            key={roleName}
            roleName={roleName}
            byModule={grouped[roleName]}
            onReload={onReload}
            t={t}
          />
        ))}
      </div>
    </div>
  );
}

// -------------------- Role card (collapsible accordion) --------------------
function RoleCard({ roleName, byModule, onReload, t }) {
  const { accessToken, user: { role: currentRole } } = useAuth();
  const canEdit = isMaster(currentRole) || isAdmin(currentRole);

  const [open, setOpen] = useState(false);
  const availableModules = Object.keys(byModule).sort((a, b) => a.localeCompare(b));

  // Local copy of ALL permissions across all modules (keyed by _id)
  const allPerms = availableModules.flatMap((m) => byModule[m]);
  const [localPerms, setLocalPerms] = useState(allPerms.map((p) => ({ ...p })));

  useEffect(() => {
    const fresh = availableModules.flatMap((m) => byModule[m]);
    setLocalPerms(fresh.map((p) => ({ ...p })));
  }, [byModule]);

  const setToggle = (index, checked) => {
    setLocalPerms((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], active: checked };
      return copy;
    });
  };

  const saveChanges = async () => {
    for (const perm of localPerms) {
      await permissionController.updatePermission(accessToken, perm._id, { active: perm.active });
    }
    onReload();
  };

  const actionIcon = (a) => {
    const icons = { edit: "edit", delete: "trash alternate outline", create: "add circle", update: "pencil alternate", view: "eye", menu: "th list" };
    return icons[a] || null;
  };

  return (
    <div className="role-card">
      <Accordion>
        <Accordion.Title
          active={open}
          onClick={() => setOpen(!open)}
          className="role-card__title"
        >
          <Icon name="dropdown" />
          {roleName}
        </Accordion.Title>

        <Accordion.Content active={open} className="role-card__content">
          <div className="permissions-module-tabs">
            <Tab
              menu={{ borderless: true, attached: false }}
              panes={availableModules.map((moduleName) => ({
                menuItem: moduleName,
                render: () => (
                  <Tab.Pane key={moduleName}>
                    <div className="role-card__perm-grid">
                    {(byModule[moduleName] || []).map((perm, index) => {
                      const localPerm = localPerms.find((lp) => lp._id === perm._id);
                      const localIndex = localPerms.findIndex((lp) => lp._id === perm._id);
                      return (
                        <div key={perm._id} className="role-card__perm-row">
                          <Icon name={actionIcon(perm.action)} className="role-card__perm-icon" />
                          <span className="role-card__perm-action">{t(perm.action)}</span>
                          <Checkbox
                            toggle
                            checked={localPerm ? localPerm.active : perm.active}
                            label={(localPerm ? localPerm.active : perm.active) ? t("enabled") : t("disabled")}
                            disabled={!canEdit}
                            onChange={(_, { checked }) => setToggle(localIndex, checked)}
                          />
                        </div>
                      );
                    })}
                    {canEdit && (
                      <div className="role-card__perm-save-cell">
                        <Button
                          primary
                          icon="check"
                          labelPosition="right"
                          content={t("save")}
                          onClick={saveChanges}
                          style={{ borderRadius: "20px", width: "180px" }}
                        />
                      </div>
                    )}
                    </div>
                  </Tab.Pane>
                ),
              }))}
            />
          </div>
        </Accordion.Content>
      </Accordion>
    </div>
  );
}

// -------------------- Pill search --------------------
function SearchPill({ dataOrigin = [], setData, filterFn, t }) {
  const [searchValue, setSearchValue] = useState("");

  const debouncedFilter = useMemo(
    () =>
      _.debounce((val) => {
        setData(dataOrigin.filter((item) => filterFn(item, val)));
      }, 300),
    [dataOrigin, setData, filterFn]
  );

  const handleChange = useCallback(
    (e) => {
      const val = e.target.value;
      setSearchValue(val);
      if (!val) {
        setData(dataOrigin);
        debouncedFilter.cancel();
        return;
      }
      debouncedFilter(val);
    },
    [dataOrigin, setData, debouncedFilter]
  );

  return (
    <div className="search-perm-wrapper">
      <img src={searchSvg} alt="search" className="search-perm-icon" />
      <input
        className="search-perm-input"
        placeholder={t("search")}
        value={searchValue}
        onChange={handleChange}
      />
    </div>
  );
}
